/**
 * QuoteAcceptDialog — Phase 2B Item H.
 *
 * Acceptation manuelle (client signe hors portail public : papier, téléphone).
 *   - Nom du signataire obligatoire (min 2 chars)
 *   - Note contextuelle optionnelle (ex. "confirmation téléphonique 03/09 15h")
 *   - Cascade identique à la signature portail : chantier, interaction, emails,
 *     acompte auto, reminders annulés.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function QuoteAcceptDialog({ open, onOpenChange, quote, onSuccess }) {
  const [signerName, setSignerName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const cs = quote?.client_snapshot || {};
      const suggested = `${cs.first_name || ""} ${cs.last_name || ""}`.trim();
      setSignerName(suggested);
      setNote("");
    }
  }, [open, quote?.client_snapshot]);

  const invalid = signerName.trim().length < 2;

  const handleSubmit = async () => {
    if (invalid) {
      toast.error("Nom du signataire requis (min 2 caractères)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/quotes/${quote.id}/accept-manual`, {
        signer_name: signerName.trim(),
        note: note.trim() || null,
      });
      toast.success("Devis accepté — chantier créé automatiquement");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="quote-accept-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Accepter le devis (client hors portail)</DialogTitle>
          <DialogDescription>
            Utilisez cette action lorsque le client a signé un papier ou confirmé
            par téléphone. La preuve reste physique (conservez le papier signé).
            Le chantier sera créé automatiquement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="accept-signer" className="text-xs text-slate-300">
              Nom du signataire
            </Label>
            <Input
              id="accept-signer"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Prénom Nom"
              maxLength={200}
              data-testid="quote-accept-signer"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="accept-note" className="text-xs text-slate-500">
              Note contextuelle (optionnel)
            </Label>
            <Textarea
              id="accept-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex. Signature papier reçue le 03/09, confirmation téléphonique 15h…"
              rows={2}
              maxLength={500}
              data-testid="quote-accept-note"
            />
          </div>
          <div className="flex items-start gap-2 rounded-md border border-amber-800/40 bg-amber-950/20 p-3 text-xs text-amber-300">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Cette acceptation manuelle n&apos;a pas la valeur juridique d&apos;une
              signature électronique. Conservez toute preuve papier / audio.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="quote-accept-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || invalid}
            className="min-w-[140px] bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
            data-testid="quote-accept-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Acceptation…
              </>
            ) : (
              "Confirmer l'acceptation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
